import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { User } from '@vimeo/viewer';
import { ViewerContext } from '@vimeo/viewer';

import PlayerLayoutGrid from '../components/PlayerLayoutGrid';
import PlayerLayoutNav from '../components/PlayerLayoutNav';
import SearchGrid from '../components/Search/SearchGrid';
import { useSearchDispatch, useSearchState } from '../components/SearchContext';
import AlbumConfigContext from '../containers/AlbumConfigContext';
import FeaturedPlayerContainer from '../containers/FeaturedPlayerContainer';
import {
  AlbumLayouts,
  AlbumThemes,
  Clip,
  ClipDataObject,
  ErrorNotification,
  Pictures,
} from '../types';
import { getDefaultThumbnailUri, getVideoId } from '../utils/helpers';

const NEXT_VIDEO = 'next';
const PREVIOUS_VIDEO = 'previous';

const Container = styled.div`
  width: 100%;
`;

type ClipDictionary = ClipDataObject[];

interface Props {
  albumId: number;
  albumIsEmpty: boolean;
  brandColor: string;
  customLogo: Pictures;
  webCustomLogo: boolean;
  description: string;
  layout: AlbumLayouts;
  name: string;
  theme: AlbumThemes;
  totalVideos: number;
  videos: ClipDataObject[];
  LoadMoreButton?: ReactNode;
  openShare: VoidFunction;
  isUpdating: boolean;
  handleError: (error: ErrorNotification) => void;
  curUser?: User;
  isOffsite?: boolean;
  allowDownloads: boolean;
  setDownloadClipId?: (id: number) => void;
  openDownloadModal?: () => void;
  allowShare: boolean;
  continuousPlay?: boolean;
  page: number;
  albumOwnerId?: number;
  teamRole?: string;
  // API returns both types depending on if the custom thumbnail is set
  pictures: Pictures | Pictures[];
  hasChosenThumbnail: boolean;
}

/**
 * Container to display playback layout
 */
function Featured({
  albumId,
  albumIsEmpty,
  brandColor,
  curUser,
  customLogo,
  webCustomLogo,
  description,
  LoadMoreButton,
  name,
  totalVideos,
  videos,
  isOffsite = false,
  allowDownloads,
  setDownloadClipId,
  openDownloadModal,
  allowShare,
  continuousPlay,
  openShare,
  page,
  isUpdating,
  albumOwnerId,
  teamRole,
  pictures,
  hasChosenThumbnail,
}: Props) {
  const viewer = useContext(ViewerContext);
  const { hashedPass } = useContext(AlbumConfigContext);
  const defaultThumbnail = useMemo(
    () => getDefaultThumbnailUri(hasChosenThumbnail ? pictures : []),
    [pictures, hasChosenThumbnail],
  );

  if (!viewer) {
    return null;
  }

  const { vimeoHttpsUrl } = viewer;
  const [skipTitleCard, setSkipTitleCard] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [searchVideoAdded, setSearchVideoAdded] = useState(false);
  const [displayedVideos, setDisplayedVideos] = useState<ClipDictionary>(videos);
  // Adding a new state playableVideos that will hold the displayedVideos + any video selected from the search result.
  const [playableVideos, setPlayableVideos] = useState<ClipDictionary>(videos);
  const { isSearching, isLoading, hasErrored, results, searchPage, searchTotal } = useSearchState();
  const searchDispatch = useSearchDispatch();

  const selectedVideo = useMemo(() => {
    if (playableVideos.length > 0) {
      return playableVideos[selectedVideoIndex];
    }
  }, [selectedVideoIndex]);

  const handleSearchVideoRemoval = () => {
    // Keeping playableVideos insync with displayed videos when the video from the search video is no longer needed.
    if (searchVideoAdded) {
      setPlayableVideos([...displayedVideos]);
      setSearchVideoAdded(false);
    }
  };

  // Load more functionality. Aggregate list with displayedVideos state.
  useEffect(() => {
    if (!isUpdating && page > 1) {
      if (searchVideoAdded) setSearchVideoAdded(false);
      setPlayableVideos([...displayedVideos, ...videos]);
      setDisplayedVideos([...displayedVideos, ...videos]);
    }
  }, [page, isUpdating]);

  const selectedVideoId = selectedVideo ? getVideoId(selectedVideo) : 0;

  const onSearchPlay = (id: number, video?: Clip) => {
    handleSearchVideoRemoval();
    let index = getVideoPositionFromId(id);
    // If selected video is not in the loaded vidoes, then adding the selected video data to playable videos.
    if (index === -1 && video) {
      setPlayableVideos([...displayedVideos, video]);
      index = displayedVideos.length;
      setSearchVideoAdded(true);
    }
    searchDispatch({ type: 'CLOSE_SEARCH' });
    selectVideo(index);
    toggleSkipTitleCard(true);
  };

  const openShareWithTracking = () => {
    openShare();
    FA.trackClick({
      container: 'album_player',
      component: 'album',
      keyword: 'share',
    });
  };

  /** Return the index position of the video in the video list from videoId
   *  Return -1 if not found
   */
  const getVideoPositionFromId = (id: number) => {
    const index = playableVideos.findIndex((video) => {
      if (!video.uri) {
        return -1;
      }
      const videoInListId = parseInt(video.uri.split('videos/')[1], 10);
      return id === videoInListId;
    });
    return index;
  };

  const selectVideo = useCallback(
    (index: number) => {
      window.scrollTo(0, 0);
      setSelectedVideoIndex(index);
    },
    [selectedVideo, selectedVideoIndex, selectedVideoId],
  );

  const cycleVideos = (direction: string) => {
    handleSearchVideoRemoval();
    if (selectedVideo) {
      const lastVideoIndex = displayedVideos.length - 1;
      const nextVideoIndex = selectedVideoIndex >= lastVideoIndex ? 0 : selectedVideoIndex + 1;
      const previousVideoIndex = selectedVideoIndex === 0 ? lastVideoIndex : selectedVideoIndex - 1;
      const index = direction === NEXT_VIDEO ? nextVideoIndex : previousVideoIndex;
      selectVideo(index);
    }
  };

  const toggleSkipTitleCard = useCallback(
    (bool: boolean) => {
      setSkipTitleCard(bool);
    },
    [setSkipTitleCard],
  );

  useEffect(() => {
    // Get config for first video on load so its ready to go
    if (!albumIsEmpty) {
      selectVideo(0);
    }
  }, []);

  return (
    <Container>
      {selectedVideo && (
        <FeaturedPlayerContainer
          albumName={name}
          brandColor={brandColor}
          customLogo={customLogo}
          webCustomLogo={webCustomLogo}
          description={description}
          onNext={() => cycleVideos(NEXT_VIDEO)}
          onPrevious={() => cycleVideos(PREVIOUS_VIDEO)}
          onPlay={() => toggleSkipTitleCard(true)}
          onStop={() => toggleSkipTitleCard(false)}
          openShare={openShareWithTracking}
          selectedVideo={selectedVideo}
          selectedVideoId={selectedVideoId}
          skipTitleCard={skipTitleCard}
          totalVideos={totalVideos}
          albumId={albumId}
          allowShare={allowShare}
          continuousPlay={continuousPlay}
          baseUrl={vimeoHttpsUrl}
          jwt={viewer.jwt}
          hashedPass={hashedPass}
          albumOwnerId={albumOwnerId}
          teamRole={teamRole}
        />
      )}
      {albumIsEmpty ? (
        <PlayerLayoutNav
          albumName={name}
          brandColor={brandColor}
          description={description}
          customLogo={customLogo}
          webCustomLogo={webCustomLogo}
          onPlay={() => toggleSkipTitleCard(true)}
          openShare={openShareWithTracking}
          videoTotal={totalVideos}
          allowShare={allowShare}
          thumbnail={defaultThumbnail}
        />
      ) : isSearching ? (
        <SearchGrid
          results={results}
          page={searchPage}
          totalVideos={searchTotal}
          isLoading={isLoading}
          hasErrored={hasErrored}
          onPlay={(id: number, isStock?: boolean, video?: Clip) => onSearchPlay(id, video)}
        />
      ) : (
        <PlayerLayoutGrid
          albumId={albumId}
          brandColor={brandColor}
          onSelect={selectVideo}
          displayedVideos={displayedVideos}
          selectedVideoIndex={selectedVideoIndex}
          curUser={curUser}
          isOffsite={isOffsite}
          allowDownloads={allowDownloads}
          setDownloadClipId={setDownloadClipId}
          openDownloadModal={openDownloadModal}
        />
      )}
      {LoadMoreButton && !isSearching ? LoadMoreButton : null}
    </Container>
  );
}

export default Featured;
